import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { BookOpen01, ClockFastForward, FileCheck02, XClose } from "@untitledui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Callout, ConfirmDialog, PageSection, notify } from "@mfa-aff/ui";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { ButtonUtility } from "@mfa-aff/ui/components/base/buttons/button-utility";
import type { PartnerMeResponse } from "@mfa-aff/shared";
import { meKey } from "../api/me";
import { onRulesRequired } from "../api/panel-signal";
import { useAcceptRules } from "../api/rules";
import { markdownLines } from "../pages/rules/markdown";

type Rules = PartnerMeResponse["rules"];

const HIDE_KEY = "mzp_rules_notice_hidden";

/** Per-browser «hide this announcement» for one announced version; storage may be unavailable. */
function hiddenVersion(): string | null {
    try {
        return window.localStorage.getItem(HIDE_KEY);
    } catch {
        return null;
    }
}
function hideVersion(v: string): void {
    try {
        window.localStorage.setItem(HIDE_KEY, v);
    } catch {
        // private window: the banner simply shows again next time
    }
}

/**
 * Rule-book notices on every panel page (apps/api partner-auth/rules-gate.ts):
 * - a new version is in force and not accepted → a warning bar with «خواندن و پذیرش» (dialog with what changed);
 * - a version is announced → «قوانین تازه از تاریخ …» with a link to the rules page (can be hidden).
 * A 403 `rules_acceptance_required` from any action opens the same dialog.
 */
export const RulesNotice = ({ rules }: { rules: Rules }) => {
    const { pathname } = useLocation();
    const qc = useQueryClient();
    const accept = useAcceptRules();
    const [open, setOpen] = useState(false);
    const [hidden, setHidden] = useState(hiddenVersion);

    useEffect(
        () =>
            onRulesRequired(() => {
                void qc.invalidateQueries({ queryKey: meKey });
                setOpen(true);
            }),
        [qc],
    );

    const onRulesPage = pathname.startsWith("/rules");
    const changes = markdownLines(rules.currentChangeSummary);
    const upcoming = rules.upcoming && !rules.upcoming.accepted ? rules.upcoming : null;

    const dialog = (
        <ConfirmDialog
            isOpen={open}
            onOpenChange={setOpen}
            icon={BookOpen01}
            title={`قوانین تازه (نسخهٔ ${rules.currentVersion}) اجرا شده است`}
            description="برای ساختن لینک یا کد تازه و درخواست تسویه، اول قوانین تازه را بپذیرید. متن کامل در صفحهٔ «قوانین» است."
            consequences={[
                ...(changes.length > 0 ? changes : ["خلاصهٔ تغییرها در صفحهٔ «قوانین» آمده است."]),
                "لینک‌ها و کدهای فعلی‌تان مثل قبل کار می‌کنند و پورسانت‌های ثبت‌شده تغییری نمی‌کنند.",
            ]}
            confirmLabel="خواندم و می‌پذیرم"
            onConfirm={async () => {
                try {
                    await accept.mutateAsync({ version: rules.currentVersion });
                    notify.success("قوانین پذیرفته شد", { description: "حالا می‌توانید دوباره لینک و کد بسازید و تسویه بخواهید." });
                } catch (e) {
                    notify.error("پذیرش انجام نشد", { description: e instanceof Error ? e.message : undefined });
                    throw e;
                }
            }}
        >
            <Button href="/rules" color="link-color" size="sm" onClick={() => setOpen(false)}>
                خواندن متن کامل قوانین
            </Button>
        </ConfirmDialog>
    );

    if (rules.mustAccept) {
        return (
            <>
                {!onRulesPage && (
                    <PageSection>
                        <Callout tone="warning" icon={FileCheck02} title="قوانین تازهٔ برنامه اجرا شده است">
                            <span className="flex flex-col items-start gap-3">
                                <span>برای ساختن لینک یا کد تازه و درخواست تسویه، قوانین تازه را بپذیرید. لینک‌ها و کدهای فعلی‌تان مثل قبل کار می‌کنند.</span>
                                <span className="flex w-full flex-wrap gap-2 max-sm:flex-col">
                                    <Button size="sm" iconLeading={FileCheck02} onPress={() => setOpen(true)}>
                                        خواندن و پذیرش
                                    </Button>
                                    <Button size="sm" color="secondary" href="/rules">
                                        متن کامل قوانین
                                    </Button>
                                </span>
                            </span>
                        </Callout>
                    </PageSection>
                )}
                {dialog}
            </>
        );
    }

    if (!upcoming || onRulesPage || hidden === upcoming.version) return dialog;
    return (
        <>
            <PageSection>
                <div className="relative">
                    <Callout tone="brand" icon={ClockFastForward} title={`قوانین تازه از تاریخ ${upcoming.effectiveFromJalali}`} className="pe-12">
                        <span className="flex flex-col items-start gap-3">
                            <span>تا آن روز قوانین فعلی معتبر است. خلاصهٔ تغییرها و متن کامل را بخوانید؛ می‌توانید از همین حالا بپذیرید.</span>
                            <Button size="sm" color="secondary" href="/rules" className="max-sm:w-full">
                                خواندن قوانین تازه
                            </Button>
                        </span>
                    </Callout>
                    <ButtonUtility
                        size="xs"
                        color="tertiary"
                        icon={XClose}
                        tooltip="بستن این پیام"
                        className="absolute end-2 top-2"
                        onPress={() => {
                            hideVersion(upcoming.version);
                            setHidden(upcoming.version);
                        }}
                    />
                </div>
            </PageSection>
            {dialog}
        </>
    );
};
